import { DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartUpdateEvent } from '@theme/events';

/**
 * Auto-advancing horizontal review slider for the cart drawer.
 */
class CartDrawerReviews extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #scrollEndTimer;

  /** @type {number | undefined} */
  #resumeTimer;

  /** @type {number} */
  #index = 0;

  /** @type {boolean} */
  #paused = false;

  /** @type {boolean} */
  #drawerOpen = false;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #hasDragged = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartScrollLeft = 0;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {HTMLElement | null} */
  #scroller = null;

  /** @type {NodeListOf<Element> | null} */
  #cards = null;

  /** @type {HTMLElement | null} */
  #scrollParent = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#refreshElements();
    if (!this.#scroller || !this.#cards || this.#cards.length === 0) return;

    const scroller = this.#scroller;
    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');

    const onOpen = () => {
      this.#refreshElements();
      this.#drawerOpen = true;
      this.#paused = false;
      this.#syncIndexFromScroll();
      if (this.#cards && this.#cards.length > 1) {
        this.#startAutoplay();
      }
    };

    const onClose = () => {
      this.#drawerOpen = false;
      this.#stopAutoplay();
    };

    if (drawer) {
      drawer.addEventListener(DialogOpenEvent.eventName, onOpen, { signal });
      drawer.addEventListener(DialogCloseEvent.eventName, onClose, { signal });
    }

    if (dialog) {
      dialog.addEventListener(
        'toggle',
        () => {
          if (dialog.open) {
            onOpen();
          } else {
            onClose();
          }
        },
        { signal }
      );

      if (dialog.open) {
        onOpen();
      }
    }

    document.addEventListener(
      CartUpdateEvent.eventName,
      () => {
        window.requestAnimationFrame(() => {
          this.#refreshElements();
          if (this.#drawerOpen && this.#cards && this.#cards.length > 1) {
            this.#startAutoplay();
          }
        });
      },
      { signal }
    );

    this.#bindDragScroll(scroller, signal);

    scroller.addEventListener(
      'scroll',
      () => {
        if (this.#isDragging) return;
        window.clearTimeout(this.#scrollEndTimer);
        this.#scrollEndTimer = window.setTimeout(() => {
          this.#syncIndexFromScroll();
        }, 120);
      },
      { signal, passive: true }
    );
  }

  #refreshElements() {
    this.#scroller = /** @type {HTMLElement | null} */ (
      this.querySelector('.cart-drawer-reviews__scroller')
    );
    this.#cards = this.#scroller?.querySelectorAll('.cart-drawer-reviews__card') ?? null;
    this.#scrollParent = /** @type {HTMLElement | null} */ (
      this.closest('scroll-hint.cart-drawer__items') ?? this.closest('.cart-drawer__items')
    );
  }

  #lockParentScroll() {
    if (this.#scrollParent) {
      this.#scrollParent.dataset.reviewsDragging = 'true';
    }
  }

  #unlockParentScroll() {
    if (this.#scrollParent) {
      delete this.#scrollParent.dataset.reviewsDragging;
    }
  }

  /**
   * @param {HTMLElement} scroller
   * @param {AbortSignal} signal
   */
  #bindDragScroll(scroller, signal) {
    const onPointerDown = (event) => {
      if (event.button !== 0) return;

      this.#isDragging = true;
      this.#hasDragged = false;
      this.#paused = true;
      this.#dragStartX = event.clientX;
      this.#dragStartScrollLeft = scroller.scrollLeft;

      scroller.classList.add('is-dragging');
      this.#lockParentScroll();
      scroller.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event) => {
      if (!this.#isDragging) return;

      const deltaX = event.clientX - this.#dragStartX;

      if (Math.abs(deltaX) > 3) {
        this.#hasDragged = true;
        event.preventDefault();
        event.stopPropagation();
      }

      scroller.scrollLeft = this.#dragStartScrollLeft - deltaX;
    };

    const onPointerEnd = (event) => {
      if (!this.#isDragging) return;

      this.#isDragging = false;
      scroller.classList.remove('is-dragging');
      this.#unlockParentScroll();

      if (scroller.hasPointerCapture(event.pointerId)) {
        scroller.releasePointerCapture(event.pointerId);
      }

      if (this.#hasDragged) {
        this.#snapToNearest();
        this.#scheduleResumeAutoplay();
      } else {
        this.#paused = false;
      }
    };

    scroller.addEventListener('pointerdown', onPointerDown, { signal });
    scroller.addEventListener('pointermove', onPointerMove, { signal, passive: false });
    scroller.addEventListener('pointerup', onPointerEnd, { signal });
    scroller.addEventListener('pointercancel', onPointerEnd, { signal });
  }

  #scheduleResumeAutoplay() {
    window.clearTimeout(this.#resumeTimer);
    this.#resumeTimer = window.setTimeout(() => {
      this.#paused = false;
    }, 800);
  }

  #startAutoplay() {
    this.#stopAutoplay();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!this.#scroller || !this.#cards || this.#cards.length < 2) return;

    const intervalMs = Number(this.dataset.interval) || 5000;
    const scroller = this.#scroller;
    const cards = this.#cards;

    this.#timer = window.setInterval(() => {
      if (this.#paused || !this.#drawerOpen || this.#isDragging) return;

      this.#index = (this.#index + 1) % cards.length;
      const card = cards[this.#index];
      if (!card) return;

      scroller.scrollTo({
        left: card.offsetLeft,
        behavior: 'smooth',
      });
    }, intervalMs);
  }

  #stopAutoplay() {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = undefined;
    }
  }

  #snapToNearest() {
    if (!this.#scroller || !this.#cards) return;

    const scrollLeft = this.#scroller.scrollLeft;
    let closest = 0;
    let minDistance = Infinity;

    this.#cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closest = i;
      }
    });

    this.#index = closest;
    const card = this.#cards[closest];
    if (!card) return;

    this.#scroller.scrollTo({
      left: card.offsetLeft,
      behavior: 'smooth',
    });
  }

  #syncIndexFromScroll() {
    if (!this.#scroller || !this.#cards) return;

    const scrollLeft = this.#scroller.scrollLeft;
    let closest = 0;
    let minDistance = Infinity;

    this.#cards.forEach((card, i) => {
      const distance = Math.abs(card.offsetLeft - scrollLeft);
      if (distance < minDistance) {
        minDistance = distance;
        closest = i;
      }
    });

    this.#index = closest;
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    if (this.#scrollEndTimer) window.clearTimeout(this.#scrollEndTimer);
    if (this.#resumeTimer) window.clearTimeout(this.#resumeTimer);
    this.#abort?.abort();
    this.#unlockParentScroll();
    this.#scroller = null;
    this.#cards = null;
    this.#scrollParent = null;
  }
}

if (!customElements.get('cart-drawer-reviews')) {
  customElements.define('cart-drawer-reviews', CartDrawerReviews);
}
